/**
 * Rule context of the vehicle detail page: the extranet base variables plus
 * the vehicle's own primitives, so a tenant rule can branch on them (e.g.
 * hide the claims card of an exited vehicle).
 */

import type { ContextVariableDescriptor } from '@components/shared/configurable';

import type { VehicleDetails } from '../../api/fleetService';
import {
  extranetBaseContextSchema,
  type ExtranetBaseContextKey,
} from '../../contextVariables';

/** Allow-list of the vehicle detail page (base + vehicle variables). */
export const vehicleContextSchema = {
  ...extranetBaseContextSchema,
  vehicle_id: {
    type: 'string',
    description: 'Identifier of the vehicle shown (route :idVeh).',
    sourceField: 'ID_VEH',
  },
  contract_id: {
    type: 'string',
    description: 'Identifier of the fleet contract of the vehicle (route :idNpol).',
    sourceField: 'ID_NPOL',
  },
  vehicle_status: {
    type: 'string',
    description: 'Vehicle status as returned by VEOS, trimmed — "EN COURS" or "SORTI".',
    sourceField: 'VEH_SITUATION',
  },
  vehicle_category: {
    type: 'string',
    description: 'Vehicle category label, e.g. "Véhicule léger (cat : A)".',
    sourceField: 'CATV_VEH',
  },
} as const satisfies Record<string, ContextVariableDescriptor>;

export type VehicleContextKey = keyof typeof vehicleContextSchema;

/** Build the vehicle page bag from the base bag (memoise on primitives). */
export function buildVehicleRuleContext(
  base: Record<ExtranetBaseContextKey, unknown>,
  ids: { idNpol: string; idVeh: string },
  details: Pick<VehicleDetails, 'statut' | 'categorie'> | null | undefined,
): Record<VehicleContextKey, unknown> {
  return {
    ...base,
    vehicle_id: ids.idVeh === '' ? undefined : ids.idVeh,
    contract_id: ids.idNpol === '' ? undefined : ids.idNpol,
    vehicle_status: details?.statut ?? undefined,
    vehicle_category: details?.categorie ?? undefined,
  };
}
